import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import MediaJournal from './MediaJournal';
import HeroRecommendations from './HeroRecommendations';
import HeroCompanion from './HeroCompanion';
import { NotebookPen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const Journal = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestAnalysis, setLatestAnalysis] = useState(null);

  const moods = {
    very_low: { label: 'Very Low', color: 'bg-red-100 text-red-800', emoji: '😞' },
    low: { label: 'Low', color: 'bg-orange-100 text-orange-800', emoji: '😔' },
    neutral: { label: 'Neutral', color: 'bg-yellow-100 text-yellow-800', emoji: '😐' },
    good: { label: 'Good', color: 'bg-green-100 text-green-800', emoji: '😊' },
    excellent: { label: 'Excellent', color: 'bg-blue-100 text-emerald-800', emoji: '😄' }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get('/journal');
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  const handleEntrySubmit = async (entryData) => {
    try {
      const response = await axios.post('/journal', entryData);
      setEntries([response.data, ...entries]);
      // Surface latest analysis at the top (hero ranking + crisis)
      if (response.data?.sentiment_analysis) {
        setLatestAnalysis(response.data.sentiment_analysis);
        setTimeout(() => {
          const el = document.querySelector('[data-testid="hero-recommendations"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
      }
      return response.data;
    } catch (error) {
      throw new Error('Failed to save journal entry');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSentimentColor = (score) => {
    if (score >= 0.7) return 'text-blue-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSentimentEmoji = (score) => {
    if (score >= 0.8) return '😊';
    if (score >= 0.6) return '🙂';
    if (score >= 0.4) return '😐';
    if (score >= 0.2) return '😔';
    return '😞';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="journal-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <NotebookPen className="w-7 h-7 text-blue-700" /> Your Wellness Journal
        </h1>
        <p className="text-gray-600">
          Express your thoughts and feelings. Our AI will provide personalized insights and activity recommendations.
        </p>
      </div>

      {/* Hero companion at top */}
      <div className="mb-6">
        <HeroCompanion variant="banner" />
      </div>

      {/* New Entry Form */}
      <div className="mb-8">
        <MediaJournal onEntrySubmit={handleEntrySubmit} />
      </div>

      {/* Hero recommendations from latest entry */}
      {latestAnalysis && (
        <div className="mb-8">
          <HeroRecommendations analysis={latestAnalysis} entryId={entries[0]?.id} />
        </div>
      )}

      {/* Journal Entries */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Recent Entries ({entries.length})
        </h2>
        
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="p-6 loading-shimmer h-40" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <Card className="glass-card p-8 text-center" data-testid="no-entries-message">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Start Your Wellness Journey
            </h3>
            <p className="text-gray-600">
              Write your first journal entry to begin receiving personalized AI insights and activity recommendations.
            </p>
          </Card>
        ) : (
          entries.map((entry, index) => (
            <Card key={entry.id} className="glass-card p-6 journal-entry" data-testid={`journal-entry-${index}`}>
              {/* Entry Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">
                    {entry.mood_level ? moods[entry.mood_level]?.emoji : '📝'}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {formatDate(entry.created_at)}
                    </p>
                    {entry.mood_level && (
                      <Badge className={`${moods[entry.mood_level]?.color} text-xs mt-1`}>
                        {moods[entry.mood_level]?.label}
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Sentiment Analysis */}
                {entry.sentiment_analysis && (
                  <div className="text-right">
                    <div className="flex items-center space-x-1">
                      <span className="text-lg">
                        {getSentimentEmoji(entry.sentiment_analysis.sentiment_score)}
                      </span>
                      <span className={`text-sm font-medium ${getSentimentColor(entry.sentiment_analysis.sentiment_score)}`}>
                        {(entry.sentiment_analysis.sentiment_score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">AI Sentiment</p>
                  </div>
                )}
              </div>
              
              {/* Entry Content */}
              <div className="mb-4">
                {entry.entry_type === 'text' ? (
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {entry.content}
                  </p>
                ) : entry.entry_type === 'audio' ? (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-2xl">🎤</span>
                      <div>
                        <p className="font-medium text-gray-900">Voice Journal Entry</p>
                        <p className="text-sm text-gray-600">{entry.content}</p>
                      </div>
                    </div>
                    {entry.audio_url && (
                      <div className="mt-2 text-sm text-blue-600">
                        Audio file: {entry.audio_url}
                      </div>
                    )}
                  </div>
                ) : entry.entry_type === 'video' ? (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-2xl">🎥</span>
                      <div>
                        <p className="font-medium text-gray-900">Video Journal Entry</p>
                        <p className="text-sm text-gray-600">{entry.content}</p>
                      </div>
                    </div>
                    {entry.video_url && (
                      <div className="mt-2 text-sm text-purple-600">
                        Video file: {entry.video_url}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {entry.content}
                  </p>
                )}
              </div>
              
              {/* AI Analysis */}
              {entry.sentiment_analysis && (
                <div className="space-y-4">
                  {/* Hero ranking + crisis (if present) — banner only on latest top-level card; respect acknowledgement */}
                  {(entry.sentiment_analysis.hero_ranking || entry.sentiment_analysis.crisis_detected) && (
                    <HeroRecommendations
                      analysis={entry.sentiment_analysis}
                      showCrisisBanner={!entry.crisis_acknowledged}
                      entryId={entry.id}
                    />
                  )}

                  {/* Emotions Detected */}
                  {entry.sentiment_analysis.emotions_detected && entry.sentiment_analysis.emotions_detected.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Emotions Detected:</p>
                      <div className="flex flex-wrap gap-2">
                        {entry.sentiment_analysis.emotions_detected.map((emotion, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs capitalize">
                            {emotion}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Recommendations */}
                  {entry.ai_recommendations && entry.ai_recommendations.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-emerald-800 mb-2">🤖 AI Recommended Activities:</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {entry.ai_recommendations.map((activity, idx) => (
                          <Badge key={idx} className="bg-blue-100 text-blue-700 capitalize">
                            Let's {activity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Supportive Message */}
                  {entry.sentiment_analysis.supportive_message && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-800 italic">
                        💙 {entry.sentiment_analysis.supportive_message}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Helpful Tips */}
      <Card className="glass-card p-6 mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 Journaling Tips</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Be Authentic</h4>
            <p>Write honestly about your thoughts and feelings. There's no right or wrong way to journal.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Regular Practice</h4>
            <p>Try to write regularly, even if it's just a few sentences about your day.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Express Freely</h4>
            <p>Don't worry about grammar or structure. Focus on expressing what you're experiencing.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Use AI Insights</h4>
            <p>Let our AI recommendations guide you to helpful activities based on your entries.</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Journal;
